import type { ChainCanvasEdge, ChainCanvasNode } from '@/components/chain/ChainCanvas';

/**
 * The editorial map, as a chain.
 *
 * WHY A CHAIN AND NOT A TREE. `ChainCanvas` places every node at an explicit
 * `(column, row)` on a CSS grid and has no layout algorithm, no collapse, no
 * zoom and no virtualization — it observes every node with a `ResizeObserver`.
 * So the shape has to be bounded by construction, not by a viewport. It is,
 * twice over: only ONE pillar is drawn at a time, and the three right-hand
 * columns are single aggregate nodes rather than one-per-topic. The widest
 * real pillar in production draws 16 nodes.
 *
 * THE COLUMNS ARE THE FOUNDER'S OWN SENTENCE — "content pillars, what's
 * underneath, plus articles, approval". Pillar, its branches, the topics filed
 * under them, the client's decision, the article. Nothing else earns a column:
 * a stage nobody can point at an object for is a label, not a stage.
 *
 * ROWS. Branches and their topics interleave: each branch takes the row of its
 * first topic, and its topics take consecutive rows after it, so a branch reads
 * as the header of the block beside it. A branch with no topics still takes a
 * row of its own — an empty branch is the single most useful thing on this
 * surface (it is the gap) and must never be collapsed away to save space.
 */

export interface EditorialBranch {
  id: string;
  name: string;
}

export interface EditorialPillar {
  id: string;
  name: string;
  weight: number;
  branches: EditorialBranch[];
}

export interface EditorialTopic {
  id: string;
  topic: string;
  /** `project_tasks.branch_id` — null while a topic is filed only to a pillar. */
  branchId: string | null;
  /** `project_tasks.status` — 'approved' once the client has said yes. */
  status: string | null;
  /** True when `artifact_kind = 'document'`, i.e. the article exists. */
  hasArticle: boolean;
}

export interface EditorialChainInput {
  pillar: EditorialPillar;
  topics: EditorialTopic[];
}

/*
 * 🔴 `decidedCount` WAS REMOVED, FOR THE REASON THE NEXT COMMENT ALREADY GIVES.
 *
 * It fed the 'Client review' aggregate node's detail line. That node is gone —
 * it was one of the two single-node columns every topic fanned into — so the
 * input became a value passed in and never read: the exact "loaded gun rather
 * than a fired one" this file already warns about below, one field along.
 * Whatever renders a decision count next should read it where decisions live
 * (`approval_decisions`), not accept it as a hint from a caller.
 */

/**
 * NO `approvedCount`. It was computed and returned and no surface ever read it
 * — `EditorialChainFlow` passes `decidedCount` IN and uses nothing back. That
 * made it a loaded gun rather than a fired one: `topics.filter(status ===
 * 'approved')` DROPS when an article is written, because filing a draft moves
 * the row from `approved` to `review` (correctly — approving an idea is not
 * approving the 1,400 words that came out of it). The first surface to render
 * it would have shown the approved count falling as the pipeline succeeded.
 *
 * Deleted rather than fixed, because the honest source for "what has a human
 * decided" is `approval_decisions`, never `project_tasks.status`: status
 * answers "where is this now" and is not monotonic, while a decision is. Any
 * count of approvals derived from status is wrong the moment work continues.
 */
export interface EditorialChain {
  nodes: ChainCanvasNode[];
  edges: ChainCanvasEdge[];
  stages: string[];
}

/*
 * 🔴 THREE COLUMNS, NOT FIVE — AND CUTTING TWO IS WHAT UNTANGLES THE PICTURE.
 *
 * 'Client review' and 'Article' used to be columns, each holding ONE aggregate
 * node, and every topic drew an edge into them. On the real ITC Tax pillar that
 * is 15 curves converging on a single point, twice — a hairball no amount of
 * styling fixes, because the convergence is in the DATA SHAPE, not the stroke.
 * Founder, looking at it: "that looks really terrible... I don't know if we
 * just get rid of that altogether."
 *
 * The mistake is a category error worth naming: review and article are not
 * PLACES a topic flows to, they are STATES a topic is in. Drawing a state as a
 * destination forces an N-to-1 fan for no information — every one of those 15
 * edges said the same thing, and the thing it said belongs on the topic itself.
 *
 * So the state moves onto the topic node, in the same vocabulary the editorial
 * table and the client desk already use (Proposed / In progress / Approved /
 * Written), and the chain becomes what it always was: pillar, its branches, and
 * the topics under them. One-to-many twice over, which is a tree, and a tree
 * draws cleanly.
 */
export const EDITORIAL_STAGES = ['Pillar', 'Branch', 'Topic'] as const;

const COL = { pillar: 0, branch: 1, topic: 2 } as const;

/** Unfiled topics group under this synthetic branch so nothing is dropped. */
export const UNFILED_BRANCH_ID = '__unfiled__';

function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

/**
 * Builds the whole canvas for one pillar. Pure and deterministic — the row
 * arithmetic is the part worth testing, and it is all here rather than in the
 * component.
 */
export function buildEditorialChain(input: EditorialChainInput): EditorialChain {
  const { pillar, topics } = input;

  const byBranch = new Map<string, EditorialTopic[]>();
  for (const branch of pillar.branches) byBranch.set(branch.id, []);
  const unfiled: EditorialTopic[] = [];
  for (const topic of topics) {
    const bucket = topic.branchId ? byBranch.get(topic.branchId) : undefined;
    if (bucket) bucket.push(topic);
    else unfiled.push(topic);
  }

  const nodes: ChainCanvasNode[] = [];
  const edges: ChainCanvasEdge[] = [];

  const ordered: Array<{ id: string; name: string; items: EditorialTopic[] }> = [
    ...pillar.branches.map((b) => ({ id: b.id, name: b.name, items: byBranch.get(b.id) ?? [] })),
  ];
  // A topic whose branch was deleted from the taxonomy is NOT dropped — it
  // surfaces here so it can be refiled. Silently vanishing is the worse bug.
  if (unfiled.length) {
    ordered.push({ id: UNFILED_BRANCH_ID, name: 'Not filed to a branch', items: unfiled });
  }

  let row = 0;
  for (const branch of ordered) {
    const branchRow = row;
    nodes.push({
      id: `branch:${branch.id}`,
      column: COL.branch,
      row: branchRow,
      label: branch.name,
      // `undefined` prints nothing; an empty branch says so in words rather
      // than showing a 0, per the surface's own honesty rule.
      detail: branch.items.length ? pluralize(branch.items.length, 'topic') : 'nothing filed yet',
    });
    edges.push({
      from: 'pillar',
      to: `branch:${branch.id}`,
      tone: branch.items.length ? 'flowing' : 'open',
    });

    if (!branch.items.length) {
      row += 1;
      continue;
    }

    for (const topic of branch.items) {
      nodes.push({
        id: `topic:${topic.id}`,
        column: COL.topic,
        row,
        label: topic.topic,
        // ONLY AN EXCEPTIONAL STATE PRINTS. This read
        // `status === 'approved' ? 'approved' : 'not decided yet'`, so on a
        // real pillar 15 of 15 cards carried the identical string "not decided
        // yet" — a value that is the same on every row is carrying no
        // information, it is just 15 lines of grey. Undecided is the DEFAULT
        // here, and the column already says so once, in the right place:
        // the Client review node reads "nothing sent yet".
        //
        // It also ignored `hasArticle` entirely, which was harmless while that
        // could never be true (the schema forbade it until 2026-09-06) and is a
        // straight lie now that articles file themselves — a topic with a
        // finished draft was still printing "not decided yet".
        /*
         * THE STATE THE TWO DELETED COLUMNS USED TO CARRY, said once, on the
         * row it belongs to. Same vocabulary as the editorial table's own
         * filter chips and the client desk's pills, so one topic does not read
         * as three different things on three surfaces.
         *
         * `undefined` for a plain proposal is deliberate: it is the DEFAULT
         * state, and a value identical on 18 of 32 rows is 18 lines of grey
         * carrying nothing. Only an exceptional state prints.
         */
        detail: topic.hasArticle
          ? 'Written'
          : topic.status === 'approved'
            ? 'Approved'
            : topic.status === 'review'
              ? 'In progress'
              : undefined,
      });
      edges.push({ from: `branch:${branch.id}`, to: `topic:${topic.id}`, tone: 'flowing' });
      row += 1;
    }
  }

  /*
   * THE THREE SINGLE-NODE COLUMNS ANCHOR TO THE TOP, NOT THE MIDDLE.
   *
   * `pillar`, `review` and `article` each hold exactly one node, and they used
   * to sit at `Math.floor((totalRows - 1) / 2)` — vertically centred against
   * the topic stack. Centring is the right instinct for a fan diagram and it
   * stops being right the moment the fan is taller than the viewport.
   *
   * Measured on the real ITC workspace at 1440x900, in BOTH themes: the column
   * headers render at y=417 and their only nodes at y=1136, inside a scroll
   * viewport 848px tall. So the pillar's own name, "Client review" and
   * "Article" were all 288px below the fold, and opening the founder's
   * headline surface showed three of five columns as labelled headers over
   * empty dot-grid. The nodes were reachable by scrolling — this was never a
   * clipping bug — but nobody scrolls to find out whether a column has
   * content; an empty column reads as an empty column.
   *
   * Row 0 puts each of the three beside the header that names it, which is
   * also how a left-to-right tree is normally drawn: root top-left, children
   * fanning down and across.
   */
  const anchorRow = 0;

  nodes.push({
    id: 'pillar',
    column: COL.pillar,
    row: anchorRow,
    label: pillar.name,
    // "0 topics" and "nothing filed here yet" are the same number and a
    // different fact. This pillar may be brand new, or it may be the gap the
    // whole surface exists to show — either way a bare 0 says neither.
    detail: topics.length ? pluralize(topics.length, 'topic') : 'nothing filed here yet',
    current: true,
  });

  /*
   * The 'Client review' and 'Article' aggregate nodes used to be pushed here,
   * each receiving one edge PER TOPIC. Those two fans were the entire reason
   * this picture read as a hairball. The facts they carried are not lost: each
   * topic now states its own status, and the counts live on the pillar node's
   * detail line, where a reader can act on them.
   */
  return { nodes, edges, stages: [...EDITORIAL_STAGES] };
}
